import type { HoleObjectModel } from '@/types/apt_generation.types';
import type { PocketObjectModel } from '@/types/pocket.types';
import { apiClient } from './client';

export const aptGenerationApi = {
  // GET /holes/{extraction_id} - get all HoleObjects for an extraction
  getHolesByExtractionId: async (extractionId: string): Promise<HoleObjectModel[]> => {
    const { data } = await apiClient.get<HoleObjectModel[]>(`/holes/${extractionId}`);
    return data;
  },

  // GET /pockets/{extraction_id} - pocket objects with nested pocket_procedures
  getPocketsByExtractionId: async (extractionId: string): Promise<PocketObjectModel[]> => {
    const { data } = await apiClient.get<PocketObjectModel[]>(`/pockets/${extractionId}`);
    return data;
  },

  // GET /chamfers/{extraction_id} - chamfer HoleObjects (object_type=Chamfer) with nested procedures
  getChamfersByExtractionId: async (extractionId: string): Promise<HoleObjectModel[]> => {
    const { data } = await apiClient.get<HoleObjectModel[]>(`/chamfers/${extractionId}`);
    return data;
  },

  // GET /png/{hole_id} - get PNG preview for a hole
  getHolePng: async (holeId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/png/${holeId}`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /png/procedure/{procedure_id} - get PNG preview for a procedure (from procedure.png_s3_url)
  getProcedurePng: async (procedureId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/png/procedure/${procedureId}`, {
      responseType: 'blob',
    });
    return data;
  },

  // PATCH /procedure/update/{procedure_id} - update procedure (e.g. procedure_name)
  updateProcedure: async (procedureId: string, body: { procedure_name: string | null }): Promise<void> => {
    await apiClient.patch(`/procedure/update/${procedureId}`, body);
  },

  // PATCH /procedure/swap-order - swap order_number of two procedures (same extraction)
  swapProcedureOrder: async (procedureIdA: string, procedureIdB: string): Promise<void> => {
    await apiClient.patch('/procedure/swap-order', {
      procedure_id_a: procedureIdA,
      procedure_id_b: procedureIdB,
    });
  },

  // GET /procedure/{procedure_id}/h-files/{filename} - get content of one H or PPlog file
  getProcedureHFile: async (procedureId: string, filename: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/procedure/${procedureId}/h-files/${filename}`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /procedure/{procedure_id}/h-files.zip - download zip of procedure H files
  downloadProcedureHFilesZip: async (procedureId: string) => {
    return await apiClient.get<Blob>(`/procedure/${procedureId}/h-files.zip`, {
      responseType: 'blob',
    });
  },

  // GET /procedure/{procedure_id}/h - the Heidenhain program the procedure machines
  getProcedureHProgram: async (procedureId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/procedure/${procedureId}/h`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /procedure/{procedure_id}/toolpath.html - interactive Plotly toolpath HTML
  getProcedureToolpathHtml: async (procedureId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/procedure/${procedureId}/toolpath.html`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /pocket-procedure/{id}/h - the Heidenhain program the pass machines
  getPocketProcedureHProgram: async (pocketProcedureId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/pocket-procedure/${pocketProcedureId}/h`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /pocket-procedure/{id}/h-files/{filename} - get H or PPlog file content for a pocket procedure
  getPocketProcedureHFile: async (pocketProcedureId: string, filename: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(
      `/pocket-procedure/${pocketProcedureId}/h-files/${encodeURIComponent(filename)}`,
      { responseType: 'blob' },
    );
    return data;
  },

  // GET /pocket-procedure/{id}/h-files.zip - download zip of H files for a pocket procedure
  downloadPocketProcedureHFilesZip: async (pocketProcedureId: string) => {
    return await apiClient.get<Blob>(`/pocket-procedure/${pocketProcedureId}/h-files.zip`, {
      responseType: 'blob',
    });
  },

  // GET /pocket-procedure/{id}/toolpath.html - interactive Plotly toolpath HTML
  // (returned as Blob so the caller can wrap it in URL.createObjectURL and
  // drop the URL into an <iframe> — iframes can't carry Bearer auth)
  getPocketProcedureToolpathHtml: async (pocketProcedureId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(
      `/pocket-procedure/${pocketProcedureId}/toolpath.html`,
      { responseType: 'blob' },
    );
    return data;
  },

  // GET /hfiles/project/{project_id} - download all H files + APT + pplog for entire project
  downloadProjectHFilesZip: async (projectId: string) => {
    return await apiClient.get<Blob>(`/hfiles/project/${projectId}`, {
      responseType: 'blob',
    });
  },

  // GET /hfiles/project/{project_id}/pockets - H files + APT + pplog for pocket extractions only
  downloadProjectPocketsHFilesZip: async (projectId: string) => {
    return await apiClient.get<Blob>(`/hfiles/project/${projectId}/pockets`, {
      responseType: 'blob',
    });
  },
};
